import { useState, useEffect } from 'react';
import styles from './CookieConsent.module.css';
import { initGoogleAnalytics, initFacebookPixel, saveUTMParameters, trackPageView } from './tracking';
import config from './config';

function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Always save UTM parameters (essential for tracking campaign source)
    saveUTMParameters();

    // Check if user has already made a choice
    const savedConsent = localStorage.getItem('cookieConsent');
    if (!savedConsent) {
      // Show banner after a short delay for better UX
      setTimeout(() => setShowBanner(true), 1000);
    } else {
      // Load saved preferences
      const savedPreferences = JSON.parse(savedConsent);
      setPreferences(savedPreferences);
      loadCookieScripts(savedPreferences);
    }
  }, []);

  const loadCookieScripts = (prefs) => {
    if (!config.enableTracking) {
      return;
    }

    // Load Google Analytics if analytics cookies are accepted
    if (prefs.analytics && config.googleAnalyticsId !== 'YOUR_GA_MEASUREMENT_ID') {
      initGoogleAnalytics(config.googleAnalyticsId);
    }

    // Load Meta/Facebook Pixel if marketing cookies are accepted
    if (prefs.marketing && config.facebookPixelId !== 'YOUR_FACEBOOK_PIXEL_ID') {
      initFacebookPixel(config.facebookPixelId);
      // Track page view after pixel is loaded
      setTimeout(() => trackPageView(), 500);
    }
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      essential: true,
      analytics: true,
      marketing: true,
    };
    setPreferences(allAccepted);
    localStorage.setItem('cookieConsent', JSON.stringify(allAccepted));
    loadCookieScripts(allAccepted);
    setShowBanner(false);
    setShowModal(false);
  };

  const handleRejectAll = () => {
    const onlyEssential = {
      essential: true,
      analytics: false,
      marketing: false,
    };
    setPreferences(onlyEssential);
    localStorage.setItem('cookieConsent', JSON.stringify(onlyEssential));
    setShowBanner(false);
    setShowModal(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookieConsent', JSON.stringify(preferences));
    loadCookieScripts(preferences);
    setShowModal(false);
    setShowBanner(false);
  };

  const openSettings = () => {
    setShowModal(true);
  };

  const togglePreference = (key) => {
    if (key === 'essential') return; // Essential cookies cannot be disabled
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (!showBanner && !showModal) return null;

  return (
    <>
      {/* Cookie Banner */}
      {showBanner && !showModal && (
        <div className={styles.bannerOverlay}>
          <div className={styles.banner}>
            <div className={styles.bannerContent}>
              <div className={styles.bannerText}>
                <h3>Wir verwenden Cookies</h3>
                <p>
                  Diese Seite verwendet Cookies, um die Effektivität unserer Werbekampagne zu messen. 
                  Wenn du deine E-Mail-Adresse eingibst, stimmst du automatisch der Verwendung von Marketing-Cookies zu.
                </p>
              </div>
              <div className={styles.bannerButtons}>
                <button 
                  className={styles.settingsButton}
                  onClick={openSettings}
                >
                  Cookie-Einstellungen
                </button>
                <button 
                  className={styles.rejectButton}
                  onClick={handleRejectAll}
                >
                  Nur Notwendige
                </button>
                <button 
                  className={styles.acceptButton}
                  onClick={handleAcceptAll}
                >
                  Alle akzeptieren
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cookie Preferences Modal */}
      {showModal && (
        <div className={styles.modalOverlay} onClick={() => setShowModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Cookie-Einstellungen</h2>
              <button 
                className={styles.closeButton}
                onClick={() => setShowModal(false)}
                aria-label="Schließen"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>

            <div className={styles.modalContent}>
              <p className={styles.modalDescription}>
                Wir verwenden Cookies, um die beste Erfahrung auf unserer Website zu gewährleisten. 
                Du kannst auswählen, welche Kategorien von Cookies du zulassen möchtest.
              </p>

              {/* Essential Cookies */}
              <div className={styles.cookieCategory}>
                <div className={styles.categoryHeader}>
                  <div className={styles.categoryTitle}>
                    <h4>Notwendige Cookies</h4>
                    <span className={styles.badge}>Immer aktiv</span>
                  </div>
                  <div className={`${styles.toggle} ${styles.toggleDisabled}`}>
                    <div className={styles.toggleSlider}></div>
                  </div>
                </div>
                <p className={styles.categoryDescription}>
                  Diese Cookies sind für die Funktion der Website erforderlich und können nicht deaktiviert werden. 
                  Sie speichern deine Cookie-Präferenzen und andere grundlegende Funktionen.
                </p>
              </div>

              {/* Analytics Cookies */}
              <div className={styles.cookieCategory}>
                <div className={styles.categoryHeader}>
                  <div className={styles.categoryTitle}>
                    <h4>Analytische Cookies</h4>
                  </div>
                  <div 
                    className={`${styles.toggle} ${preferences.analytics ? styles.toggleActive : ''}`}
                    onClick={() => togglePreference('analytics')}
                  >
                    <div className={styles.toggleSlider}></div>
                  </div>
                </div>
                <p className={styles.categoryDescription}>
                  Diese Cookies helfen uns zu verstehen, wie Besucher mit unserer Website interagieren, 
                  indem sie Informationen anonym sammeln und melden (z.B. Google Analytics).
                </p>
              </div>

              {/* Marketing Cookies */}
              <div className={styles.cookieCategory}>
                <div className={styles.categoryHeader}>
                  <div className={styles.categoryTitle}>
                    <h4>Marketing Cookies</h4>
                  </div>
                  <div 
                    className={`${styles.toggle} ${preferences.marketing ? styles.toggleActive : ''}`}
                    onClick={() => togglePreference('marketing')}
                  >
                    <div className={styles.toggleSlider}></div>
                  </div>
                </div>
                <p className={styles.categoryDescription}>
                  Diese Cookies werden verwendet, um die Wirksamkeit unserer Werbekampagnen zu messen 
                  und dir relevante Anzeigen zu zeigen (z.B. Facebook Pixel, Instagram Tracking).
                </p>
              </div>
            </div>

            <div className={styles.modalFooter}>
              <button 
                className={styles.rejectAllButton}
                onClick={handleRejectAll}
              >
                Alle ablehnen
              </button>
              <div className={styles.buttonContainer}>
                <button 
                    className={styles.saveButton}
                    onClick={handleSavePreferences}
                >
                    Auswahl speichern
                </button>
                <button 
                    className={styles.acceptAllButton}
                    onClick={handleAcceptAll}
                >
                    Alle akzeptieren
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default CookieConsent;

// Helper functions you can export and use in your app
export const getCookieConsent = () => {
  const saved = localStorage.getItem('cookieConsent');
  return saved ? JSON.parse(saved) : null;
};

export const hasAnalyticsConsent = () => {
  const consent = getCookieConsent();
  return consent?.analytics === true;
};

export const hasMarketingConsent = () => {
  const consent = getCookieConsent();
  return consent?.marketing === true;
};

// Auto-accept cookies when user submits form (since they're giving us their email for marketing)
export const acceptMarketingCookiesForFormSubmission = () => {
  const allAccepted = {
    essential: true,
    analytics: true,
    marketing: true,
  };
  localStorage.setItem('cookieConsent', JSON.stringify(allAccepted));
  
  // Load tracking scripts if not already loaded
  const config = require('./config').default;
  const { initGoogleAnalytics, initFacebookPixel } = require('./tracking');
  
  if (config.enableTracking) {
    if (config.googleAnalyticsId !== 'YOUR_GA_MEASUREMENT_ID') {
      initGoogleAnalytics(config.googleAnalyticsId);
    }
    if (config.facebookPixelId !== 'YOUR_FACEBOOK_PIXEL_ID') {
      initFacebookPixel(config.facebookPixelId);
    }
  }
};

